import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './ExternalEditorContent.module.scss';
import UploadedVersionsList from '../UploadedVersionsList/UploadedVersionsList';
import CardMenu from '../../../../components/CardMenu/CardMenu';
import useFixedCardMenu from '../../hooks/useFixedCardMenu';
import useEditorResize from '../../hooks/useEditorResize';
import { useUIContext } from '../../../../context/UIContext';
import { useUserContext } from '../../../../context/UserContext';
import { v4 as uuidv4 } from 'uuid';
import RecordClipTrigger from '../../../../components/RecordClipTrigger/RecordClipTrigger';
import Player from '../../../../components/Player';
import UploadExternalVersionModal from '../UploadExternalVersionModal/UploadExternalVersionModal';
import ResourceHelper from '../../../../utils/ResourceHelper';
import VideoService from '../../../../service/VideoService';
import { ToastNotification } from '../../../../components/Toast/Toast';
import { useTranslation } from 'react-i18next';

const ExternalEditorContent = ({ story, onNewVersionUploaded }) => {

    const pageContentRef = useRef(null);
    const { t } = useTranslation();
    const { showMenu, menuCoordinates, menuRef, toggleShowMenu, onOpenMenu } = useFixedCardMenu({});

    const [indexInPlayer, setIndexInPlayer] = useState(0);

    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

    const { user } = useUserContext();

    const { miniSidebar } = useUIContext();

    const [versionsList, setVersionsList] = useState([]);
    const [loading, setLoading] = useState(false);

    const preparedVersionList = useMemo(() => {
        if (!versionsList) {
            return [];
        }
        const orderedList = versionsList.sort((a, b) => a.createdAt - b.createdAt).map((version) => {
            return { ...version, author: { avatar: user.avatarUri, name: user.userFullName }, video: version.url, ...version.properties };
        });
        // Always add a card for new version
        orderedList.push({
            id: uuidv4(),
            video: null,
            name: '',
            draft: true,
            author: { avatar: user.avatarUri, name: user.userFullName }
        })

        return orderedList;
    }, [story, user, versionsList]);

    const { clipsListStyles, editorRef } = useEditorResize({
        playerClassName: 'Player',
        miniSidebar,
        miniRightSidebar: false,
        videoSliderChangedFlag: false,
        pageContentRef,
    });

    const handleChooseVersion = (index) => {
        setIndexInPlayer(Number(index));
    }

    const onUploadNewVersion = () => {
        setIsUploadModalOpen(true);
    }

    const handleRemove = useCallback(async (id) => {
        const versionToDelete = preparedVersionList.find((version) => version.id === id);
        setLoading(true);
        try {
            await VideoService.deleteExternalVideoVersion(story.id, id);
            const versionIndex = preparedVersionList.indexOf(versionToDelete);
            if (versionIndex === indexInPlayer ?? versionIndex) {
                setIndexInPlayer(indexInPlayer === 0 ? 0 : indexInPlayer - 1);
            }
            setVersionsList(prevState => prevState.filter((version) => version.id !== id));
            ToastNotification({ message: t('edit_external.versions.remove.succeeded', { name: versionToDelete.name }), iconName: 'success' });
        } catch (e) {
            ToastNotification({
                message: t('edit_external.versions.remove.error', { name: versionToDelete.name }),
            })
        } finally {
            setLoading(false);
        }
    }, [versionsList, indexInPlayer]);

    useEffect(() => {
        const externalUploads = ResourceHelper.getAllResourcesByType(story, 'externalUpload') ?? [];
        setVersionsList(externalUploads);
    }, [story])

    return (
        <>
            <div className={styles.wrapper} ref={pageContentRef}>
                <div ref={editorRef} className={styles.playerWrapper}>
                    <Player
                        src={preparedVersionList[indexInPlayer]?.video}
                        uuid={preparedVersionList[indexInPlayer]?.id}
                        overlay={true}
                        active={!preparedVersionList[indexInPlayer]?.video}
                        isDraft={!preparedVersionList[indexInPlayer]?.video && <RecordClipTrigger
                            handleUpload={onUploadNewVersion}
                        />}
                    />
                </div>
                <UploadedVersionsList loading={loading} versionsList={preparedVersionList} activeVersion={preparedVersionList[indexInPlayer]} showMenu={showMenu} onSelect={handleChooseVersion} onOpenMenu={onOpenMenu} />
            </div>
            {showMenu.flag ? (
                <CardMenu
                    customStyles={{ top: menuCoordinates.y, left: menuCoordinates.x }}
                    menuRef={menuRef}
                    listMode={false}
                    clipsListStyles={clipsListStyles}
                    toggleShowMenu={toggleShowMenu}
                    item={preparedVersionList[showMenu?.index]}
                    deleteVideo={!preparedVersionList[showMenu.index]?.draft && handleRemove}
                    uploadItem={preparedVersionList[showMenu.index]?.draft ? {
                        icon: "uploadVideo",
                        action: onUploadNewVersion,
                    } : null}
                />
            ) : null}

            <UploadExternalVersionModal storyId={story.id} open={isUploadModalOpen} onUploaded={onNewVersionUploaded} onClose={() => setIsUploadModalOpen(false)} />
        </>
    )

}

export default ExternalEditorContent